/**
 * Crypto utility functions for HMAC and checksum verification
 */

/**
 * Generate HMAC-SHA256 checksum of data using a secret key
 * @param data - The data to hash (will be stringified to JSON)
 * @param secret - The secret key for HMAC
 * @returns Hex-encoded HMAC-SHA256 checksum
 */
export async function generateHMAC(data: any, secret: string): Promise<string> {
  const encoder = new TextEncoder();
  
  // Convert secret to key
  const keyData = encoder.encode(secret);
  const key = await crypto.subtle.importKey(
    'raw',
    keyData,
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign']
  );
  
  // Stringify data to JSON (excluding responseChecksum if present)
  const dataToHash = { ...data };
  delete (dataToHash as any).responseChecksum;
  const messageData = encoder.encode(JSON.stringify(dataToHash));
  
  // Generate HMAC
  const signature = await crypto.subtle.sign('HMAC', key, messageData);
  
  // Convert to hex string
  const hashArray = Array.from(new Uint8Array(signature));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  
  return hashHex;
}

/**
 * Verify response checksum using HMAC-SHA256
 * @param responseData - The response data from the API
 * @param nonce - The nonce used for the request
 * @param receivedChecksum - The checksum received from the server
 * @returns true if verification succeeds, false otherwise
 */
export async function verifyResponseChecksum(
  responseData: any,
  nonce: string,
  receivedChecksum: string
): Promise<boolean> {
  try {
    const expectedChecksum = await generateHMAC(responseData, nonce);
    return expectedChecksum === receivedChecksum;
  } catch (error) {
    console.error('Checksum verification error:', error);
    return false;
  }
}
